import {
  Box,
  Button,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@/common/styles/style";
import { useEffect, useState } from "react";
import useCheckbox from "@/hooks/useCheckbox";
import SearchBox from "@/molecules/searchBox";
import { styles } from "./style";
import Dialog from "@/molecules/dialog";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { CollectedDataType, apiMachineType } from "@/common/type/type";

interface DataModifyProps {
  open: boolean;
  selectedMachine: apiMachineType;
  onSave: (data: CollectedDataType[]) => void;
  onClose: () => void;
  selectedNames?: string[];
}

export default function DataModify({
  open,
  selectedMachine,
  onSave,
  onClose,
  selectedNames,
}: DataModifyProps) {
  const [filteredData, setFilteredData] = useState<CollectedDataType[]>([]);
  const {
    checkedItems,
    setCheckedItems,
    handleCheckboxChange,
    handleParentCheckboxChange,
    resetCheckboxes,
  } = useCheckbox(filteredData.length || 0);
  const [search, setSearch] = useState({ key: "", value: "" });
  const [selectedChipsOrder, setSelectedChipsOrder] = useState<string[]>([]);
  const [updated, setUpdated] = useState<CollectedDataType[] | null>(null);

  useEffect(() => {
    resetCheckboxes();
    setSelectedChipsOrder([]);
    setUpdated([]);
  }, []);

  useEffect(() => {
    if (filteredData.length > 0 && selectedNames) {
      const newCheckedState = filteredData.map((data) =>
        selectedNames.includes(data.dataName)
      );
      setCheckedItems(newCheckedState);
    }
  }, [filteredData, selectedNames, setCheckedItems]);
  
  useEffect(() => {
    if (open) {
      const checkServerConnection = async () => {
        try {
          const response = await fetch(`/api/dashboard/widget/item/${selectedMachine.id}`, {
              method: "GET",
              credentials: "include",
          });

          const data = await response.json();
          if (response.ok) {
            console.log(data.message);
            setFilteredData(data.result);
          } else {
            console.error("サーバ接続エラー: ", response.statusText);
          }
        } catch (error) {
          console.error("サーバ接続エラー:", error);
        }
      };

      checkServerConnection();
    }
  }, [open, selectedMachine.id]);

  useEffect(() => {
    const newOrder = checkedItems
      .map((checked, index) => (checked ? filteredData[index].dataName : ""))
      .filter(Boolean);

    const updatedData =
      checkedItems.length > 0 && checkedItems.some((item) => item === true)
        ? filteredData.filter((_, index) => checkedItems[index])
        : [];

    setSelectedChipsOrder(newOrder);
    setUpdated(updatedData);
  }, [checkedItems, filteredData]);

  const handleSearch = (searchType: string, value: string) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      [searchType]: value,
    }));
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const updatedChipsOrder = Array.from(selectedChipsOrder as string[]);
    const [removed] = updatedChipsOrder.splice(source.index, 1);
    updatedChipsOrder.splice(destination.index, 0, removed);

    setSelectedChipsOrder(updatedChipsOrder);
  };

  const handleSave = async () => {
    const sortedData = selectedChipsOrder?.map((chipName) =>
      updated?.find((item) => item.dataName === chipName)
    );
    onSave(sortedData as CollectedDataType[]);
    onClose();
  };

  const selectedChips = (selectedChipsOrder as string[])
    .map((key) => filteredData.find((data) => data.dataName === key))
    .filter(Boolean)
    .map((data) => (
      <Chip key={data?.dataName} label={data?.dataName} sx={styles.chip} />
    ));

  return (
    <Dialog
      open={open}
      title="データ表示編集"
      onClose={onClose}
      sx={styles.container}
      contentChildren={
        <Box>
          <Stack direction="row" spacing={5}>
            <Box>
              <SearchBox onSearch={(value) => handleSearch("key", value)} />
              <Box sx={styles.tableArea}>
                <Table sx={styles.table}>
                  <TableHead>
                    <TableRow sx={styles.tableRow}>
                      <TableCell align="center" sx={styles.tableCell}>
                        <Checkbox
                          checked={
                            checkedItems.length > 0 &&
                            checkedItems.every((item) => item)
                          }
                          indeterminate={
                            checkedItems.some((item) => item) &&
                            !checkedItems.every((item) => item)
                          }
                          onChange={(e) => handleParentCheckboxChange(e)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={styles.tableCell}>
                        <Typography variant="body1">変数</Typography>
                      </TableCell>
                      <TableCell align="center" sx={styles.tableCell}>
                        <Typography variant="body1">値</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .filter((data) =>
                        data.dataName
                          .toLowerCase()
                          .includes(search.key.toLowerCase())
                      )
                      .map((data, index) => (
                        <TableRow sx={styles.tableRow} key={`dataMod-${index}`}>
                          <TableCell
                            align="center"
                            width="20%"
                            sx={styles.tableCell}
                          >
                            <Checkbox
                              checked={checkedItems[index] || false}
                              onChange={(e) => handleCheckboxChange(index, e)}
                            />
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            <Typography variant="body1" p={1}>
                              {data.dataName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={styles.tableCell}>
                            <Typography variant="body1" p={1}>
                              {data.value}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
            <Box>
              <Typography variant="h6">ドラックして並び替え</Typography>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <Box
                      ref={provided.innerRef}
                      sx={{
                        ...styles.chipContainer,
                      }}
                      {...provided.droppableProps}
                    >
                      {selectedChips.map((chip, index) => (
                        <Draggable
                          key={index}
                          draggableId={String(index)}
                          index={index}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              {chip}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          </Stack>
        </Box>
      }
      actionsChildren={
        <Button variant="contained">
          <Typography variant="button" onClick={handleSave}>
            保存
          </Typography>
        </Button>
      }
    />
  );
}
